import classNames from 'classnames';
import dayjs from 'dayjs';
import {
  AllDayContentArg,
  DayCellContentArg,
  DayHeaderContentArg,
  EventContentArg,
  MoreLinkContentArg,
  SlotLabelContentArg,
} from '@fullcalendar/core';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { getBorderClassNames } from '@nx-frontend/shared/themes';
import { PortalBorderType, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { CompleteTask } from './complete-task';
import { MorePopoverState } from './store';
import { getDayTasks } from './util';

export function renderDayMoreLinkContent(moreLinkInfo: MoreLinkContentArg) {
  return (
    <TippyTooltip message={<div style={{ whiteSpace: 'normal', textAlign: 'center' }}>Click to show</div>}>
      <span className="calendar-more-button" data-hidden-tasks={moreLinkInfo.num}>
        {`${moreLinkInfo.num} more`}
      </span>
    </TippyTooltip>
  );
}

export function AddTaskButtonSmall({ onCreateTask, date }: { onCreateTask: (date?: Date) => void; date: Date }) {
  const allDayDate = dayjs(date).add(12, 'hour').toDate();
  return (
    <button className="rounded my-1 invisible add-task-button" onClick={() => onCreateTask(allDayDate)}>
      <Icon icon={IconsMap.PLUS} className="add-task-button-icon w-4" />
    </button>
  );
}

export function renderDayHeaderContent(
  {
    isMobile,
    allowCreateNewTask,
    onCreateTask,
  }: { isMobile: boolean; allowCreateNewTask?: boolean; onCreateTask: (date?: Date) => void },
  headerInfo: DayHeaderContentArg
) {
  return isMobile ? (
    headerInfo.view.type !== 'timeGridDay' ? (
      <div className="flex flex-col justify-between items-center">
        <span className="weekday">{dayjs(headerInfo.date).format('dd').slice(0, 1)}</span>
        <span
          className={classNames('daynumber', {
            today: headerInfo.isToday,
          })}
        >
          {dayjs(headerInfo.date).format('D')}
        </span>
      </div>
    ) : null
  ) : (
    <div className="flex justify-between items-center">
      <span>{headerInfo.text.split(' ').reverse().join(' ')}</span>
      {allowCreateNewTask && <AddTaskButtonSmall onCreateTask={onCreateTask} date={headerInfo.date} />}
    </div>
  );
}

export function renderMonthHeaderContent({ isMobile }: { isMobile: boolean }, headerInfo: DayHeaderContentArg) {
  return isMobile ? headerInfo.text.slice(0, 1) : headerInfo.text;
}

export function renderMonthDayCellContent(
  {
    isMobile,
    allowCreateNewTask,
    onCreateTask,
    morePopover,
    themeColors,
    themeStyle,
  }: {
    isMobile: boolean;
    allowCreateNewTask?: boolean;
    onCreateTask: (date?: Date) => void;
    morePopover: MorePopoverState | null;
    themeColors?: ThemeColorSchemas;
    themeStyle?: PortalStyleConfig;
  },
  dayInfo: DayCellContentArg
) {
  if (!isMobile) {
    return (
      <>
        <span className="fc-day-cell-number">{dayInfo.dayNumberText}</span>
        {allowCreateNewTask && <AddTaskButtonSmall onCreateTask={onCreateTask} date={dayInfo.date} />}
      </>
    );
  } else {
    const date = dayInfo.date;
    const tasks = dayInfo.view.calendar.getEvents();
    const dayTasks = getDayTasks(date, tasks);
    const isActiveCell = morePopover !== null && dayjs(date).isSame(morePopover.date, 'day');
    return (
      <div
        className={classNames(
          'mobile-month-day',
          { 'is-active': isActiveCell },
          isActiveCell &&
            themeColors &&
            themeStyle &&
            getBorderClassNames(themeColors, themeStyle, PortalBorderType.PRIMARY)
        )}
      >
        <span className="fc-day-cell-number">{dayInfo.dayNumberText}</span>
        <div className="fc-day-cell-mobile-events flex items-center justify-center">
          {Array(dayTasks.length)
            .fill(0)
            .slice(0, 3)
            .map((_) => (
              <span className="fc-day-cell-mobile-event-counter block bg-neutral-500 rounded"></span>
            ))}
        </div>
      </div>
    );
  }
}

export function renderWeekAllDayContent({ isMobile }: { isMobile: boolean }, allDayContent: AllDayContentArg) {
  return !isMobile ? 'All-day' : null;
}

export function renderDayAllDayContent({ isMobile }: { isMobile: boolean }, allDayContent: AllDayContentArg) {
  return isMobile ? (
    <div className="fc-dayView-allDay flex flex-col items-center justify-center">
      <span className="weekday">{dayjs(allDayContent.view.currentStart).format('ddd')}</span>
      <span
        className={classNames('daynumber', {
          today: dayjs().isSame(dayjs(allDayContent.view.currentStart), 'day'),
        })}
      >
        {dayjs(allDayContent.view.currentStart).format('D')}
      </span>
    </div>
  ) : (
    'All-day'
  );
}

export function renderEventContent(
  { completeTask }: { completeTask: (taskId: string, done: boolean) => void },
  eventInfo: EventContentArg
) {
  const isDone = 'done' in eventInfo.event.extendedProps && eventInfo.event.extendedProps.done ? true : false;
  return (
    <span
      className={classNames('calendar-event w-full whitespace-nowrap cursor-pointer flex items-center', {
        done: isDone,
      })}
      data-date={eventInfo.event.start?.getTime()}
      data-title={eventInfo.event.title}
      data-id={eventInfo.event.id}
      data-completed={isDone}
    >
      <CompleteTask isDone={isDone} taskId={eventInfo.event.id} completeTask={completeTask} />
      <TippyTooltip
        message={
          <div style={{ maxWidth: '120px', whiteSpace: 'normal', textAlign: 'center' }}>{eventInfo.event.title}</div>
        }
      >
        <span className="fc-event-name block overflow-hidden text-ellipsis">{eventInfo.event.title}</span>
      </TippyTooltip>
    </span>
  );
}

export function renderDayEventContent(
  { isMobile, completeTask }: { isMobile: boolean; completeTask: (taskId: string, done: boolean) => void },
  eventInfo: EventContentArg
) {
  const isDone = 'done' in eventInfo.event.extendedProps && eventInfo.event.extendedProps.done ? true : false;
  const deadline =
    'originalTime' in eventInfo.event.extendedProps && eventInfo.event.extendedProps.originalTime !== null
      ? eventInfo.event.extendedProps.originalTime
      : eventInfo.event.start;
  return (
    <span
      className={classNames('calendar-event w-full whitespace-nowrap flex items-center', {
        done: isDone,
      })}
      data-date={eventInfo.event.start?.getTime()}
      data-title={eventInfo.event.title}
      data-id={eventInfo.event.id}
      data-completed={isDone}
    >
      <CompleteTask isDone={isDone} taskId={eventInfo.event.id} completeTask={completeTask} />
      <TippyTooltip
        message={
          <div style={{ maxWidth: '120px', whiteSpace: 'normal', textAlign: 'center' }}>{eventInfo.event.title}</div>
        }
      >
        {eventInfo.timeText ? (
          <span className="fc-event-name flex overflow-hidden cursor-pointer justify-between w-full">
            <span className="overflow-hidden text-ellipsis grow-0" style={!isMobile ? { maxWidth: '270px' } : {}}>
              {eventInfo.event.title}
            </span>
            <span className="mx-1 text-neutral-400">{dayjs(deadline).format('h:mma')}</span>
          </span>
        ) : (
          <span className="fc-event-name block overflow-hidden text-ellipsis">{eventInfo.event.title}</span>
        )}
      </TippyTooltip>
    </span>
  );
}

export function renderSlotLabelContent({ isMobile }: { isMobile: boolean }, slotInfo: SlotLabelContentArg) {
  return (
    <div className="flex justify-center w-full h-full">{dayjs(slotInfo.date).format(isMobile ? 'H:mm' : 'ha')}</div>
  );
}

export function renderMonthMoreLinkContent(moreLinkInfo: MoreLinkContentArg) {
  return (
    <TippyTooltip message={<div style={{ whiteSpace: 'normal', textAlign: 'center' }}>Click to show</div>}>
      <span>{`${moreLinkInfo.num} more`}</span>
    </TippyTooltip>
  );
}

export function renderWeekMoreLinkContent({ isMobile }: { isMobile: boolean }, moreLinkInfo: MoreLinkContentArg) {
  return !isMobile ? (
    <TippyTooltip message={<div style={{ whiteSpace: 'normal', textAlign: 'center' }}>Click to show</div>}>
      <span className="calendar-more-button block w-full" data-hidden-tasks={moreLinkInfo.num}>
        {`${moreLinkInfo.num} more`}
      </span>
    </TippyTooltip>
  ) : (
    <span className="calendar-more-button block w-full" data-hidden-tasks={moreLinkInfo.num}>
      {`${moreLinkInfo.num} tasks`}
    </span>
  );
}
